// src/hooks/useImagePicker.ts
import { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ImageTypeEnums,
  OpenImagePickerType,
  ISelectedImage,
  selectImages,
  uploadImageRequest,
  uploadImageSuccess,
  uploadImageFailure,
  removeImage,
  resetImages,
} from 'core/store/upload/actions';
import { renderFileUUId } from 'core/utils/helper';
import useNativePermission from './useNativePermission';

export const useGetImagePicker = (name: string, multiple: boolean = false) => {
  const imageState = useSelector((state: any) => state.Upload?.selectedImages); // Adjust according to your state shape
  const images = useMemo(() => {
    return imageState.filter((e: ISelectedImage) => e.name === name)
  }, [imageState, name])
  
  return multiple ? (images || []) : images[0]
}

export const useRemoveImage = () => {
  const dispatch = useDispatch();

  const onRemove = (uuid: string) => {
    dispatch(removeImage(uuid))
  }

  return onRemove
}

export const useResetImages = () => {
  const dispatch = useDispatch();

  const onReset = () => {
    dispatch(resetImages())
  }

  return onReset
}

const useImagePicker = (props: any) => {
  const {
    initData,
    multiple = false,
    handleChange,
    name,
    property = ImageTypeEnums.TEMPORARY,
    openType = OpenImagePickerType.GALLERY,
    fileType = "IMAGE",
    maxFile,
  } = props

  const dispatch = useDispatch();
  const { checkPermission, requestPermission } = useNativePermission()
  const [ selectedImages, setSelectedImages ] = useState<ISelectedImage[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null); // Ref for the file input
  const imageState = useGetImagePicker(name, multiple)

  const [openModalFail, setOpenModalFail] = useState<boolean>(false);

  useEffect(() => {
    if (initData) {
      if (multiple) {
        setSelectedImages(initData);
        dispatch(selectImages(initData.map((e: ISelectedImage) => ({
          ...e,
          name,
          uploadType: 'SERVER'
        }))));
      } else {
        setSelectedImages([initData]);
        dispatch(selectImages({
          ...initData,
          name,
          uploadType: 'SERVER'
        }));
      }
    }
  // eslint-disable-next-line
  }, [initData])

  useEffect(() => {
    if (multiple) {
      const filesUpload = imageState
        .filter((e: ISelectedImage) => e.uploadType === 'UPLOAD')
      if (filesUpload.every((e: ISelectedImage) => e.src)) {
        handleChange?.(filesUpload)
      }
    } else {
      if (imageState?.src && imageState.uploadType === 'UPLOAD') {
        handleChange?.(imageState)
      }
    }
  }, [imageState, handleChange, multiple])

  const handleUpload = (images: ISelectedImage[]) => {
    if (images.length > 0) {
      dispatch(selectImages(multiple ? images : images[0]));
      images.forEach(file => {
        dispatch(uploadImageRequest(file)); // Upload each file
      });
    }
  };

  const handleImageChange = (files: FileList | null) => {
    if (!files) return;

    const newImages: ISelectedImage[] = Array.from(files).map((file) => ({
      uuid: renderFileUUId(file), // Unique ID for each image
      file: file,
      type: property,
      src: '',
      name,
      uploadType: 'UPLOAD',
    }));

    handleUpload(newImages);

    setSelectedImages((prevImages) => (
      multiple ? [...prevImages, ...newImages] : newImages
    ));

    Array.from(files).forEach((file, index) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImages((prevImages) =>
          prevImages.map((img, i) =>
            i === (multiple ? prevImages.length - newImages.length + index : index)
              ? { ...img, src: reader.result as string, loading: false }
              : img
          )
        );
      };
      reader.readAsDataURL(file);
    });
  };

  const handleUploadFail = (uuid: string) => {
    dispatch(uploadImageFailure({uuid, name}));
    // setSelectedImages((prevImages) => prevImages.filter((img) => img.uuid !== uuid));
  };

  const handleRetryUpload = () => {
    dispatch(selectImages(imageState));
    if (multiple) {
      imageState.map((e: any) => {
        (window as any).webViewRequestRetryUpload(e.uuid)
      })
    } else {
      (window as any).webViewRequestRetryUpload(imageState.uuid)
    }
  };


  const handleRemoveImage = (uuid: string) => {
    console.log(uuid)
    // dispatch(uploadImageFailure({uuid, name}));
    // setSelectedImages((prevImages) => prevImages.filter((img) => img.uuid !== uuid));
  };

  const addNameToResponse = (data: any, openType: OpenImagePickerType) => {
    return data.map((e: any) => ({
      ...e,
      name,
      uploadType: 'UPLOAD',
      openType
    }))
  }

  const createNativeEvent = (openType: OpenImagePickerType) => {
    (window as any)._nativeResponseFileUploading = function(data: any[]) {
      data = addNameToResponse(data, openType) as ISelectedImage[]
      dispatch(selectImages(multiple ? data : data[0]));
      setSelectedImages(data as ISelectedImage[])
    };
    (window as any)._nativeResponseFileSuccess = function(data: any) {
      dispatch(uploadImageSuccess({...data, name}));
    };
    (window as any)._nativeResponseFileFail = function(uuid: string) {
      handleUploadFail(uuid)
    };
  }

  const handleButtonClick = () => {
    const openPhoto = (data?:any) => {
      if(data && Object.hasOwn(data, "hasNotification")) {
          return
      }
      (window as any).webviewRequestTakePhoto(
        multiple ? "MULTIPLE" : "SINGLE",
        fileType,
        property
      );
    }
    const openGallery = (data?:any) => {
      if(data && Object.hasOwn(data, "hasNotification")) {
          return
      }
      (window as any).webviewRequestChooseFile?.(
        multiple ? "MULTIPLE" : "SINGLE",
        fileType,
        property,
        maxFile
      );
    }
    switch ((window as any)._APP_PLATFORM.toLowerCase()) {
      case 'ios':
      case 'android':
        if (openType === OpenImagePickerType.CAMERA) {
          // Call native camera
          if (!checkPermission('camera')) {
            requestPermission('camera', openPhoto)
          } else {
            openPhoto()
          }
          createNativeEvent(OpenImagePickerType.CAMERA)
        } else {
          // Call native gallery
          if (!checkPermission('library')) {
            requestPermission('library', openGallery)
          } else {
            openGallery(); // Call native image picker
          }
          createNativeEvent(OpenImagePickerType.GALLERY)
        }
        break;
      default:
        fileInputRef.current?.click(); // Trigger click on hidden file input
        break;
    }
  };

  const handleSubmitFail = () => {
    setOpenModalFail(false)
  }

  return { 
    selectedImages, 
    handleImageChange, 
    handleRemoveImage, 
    fileInputRef, // Expose the file input reference
    handleButtonClick, // Expose the button click handler
    imageState: multiple ? imageState : (imageState ? [imageState] : []),
    openModalFail,
    setOpenModalFail,
    handleSubmitFail,
    handleRetryUpload,
  };
};

export default useImagePicker;
